import { useEffect, useState, useCallback } from 'react';
import { api } from '../app/api';
import { logger } from '../lib/logger';

/**
 * useLockerRoomEntitlement — single source of truth for Locker Room access.
 *
 * Phase 20B (current):
 *   - Calls `GET /api/billing/locker-room-pass/entitlement`. The backend
 *     resolves the caller via the session cookie (or returns the free shape
 *     for unauthenticated guests).
 *   - Returned shape: `{ isPro, source, current_period_end, plan_key, status, loading, refresh }`.
 *   - On network errors the hook fails CLOSED (isPro: false) so we never
 *     accidentally unlock premium content for a free user.
 *
 * Why this hook (and not a Provider/Context):
 *   - The entitlement is only consumed by a handful of components
 *     (`LockerRoom`, `FloatingMusicPlayer`, `LockerRoomPassModal`,
 *     `LockerRoomWelcome`). A hook lets each consumer fetch fresh state
 *     after a purchase without rebuilding a context tree.
 *   - Each hook call uses an in-memory cache to dedupe parallel requests.
 */

const ENTITLEMENT_PATH = '/api/billing/locker-room-pass/entitlement';

// Module-level in-flight cache: dedupes multiple components mounting at the
// same time (e.g. LockerRoom page + FloatingMusicPlayer). Resolves to the
// same Promise so we make one network call per render cycle.
let _inflight = null;
let _cached = null;
let _cachedAt = 0;
const CACHE_TTL_MS = 15_000;

function fetchEntitlement() {
  const now = Date.now();
  if (_cached && now - _cachedAt < CACHE_TTL_MS) {
    return Promise.resolve(_cached);
  }
  if (_inflight) return _inflight;
  _inflight = api.get(ENTITLEMENT_PATH)
    .then((data) => {
      _cached = data || { isPro: false, source: 'free' };
      _cachedAt = Date.now();
      return _cached;
    })
    .catch((err) => {
      logger.warn('useLockerRoomEntitlement: fetch failed, defaulting to free', err);
      _cached = { isPro: false, source: 'free' };
      _cachedAt = Date.now();
      return _cached;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

/**
 * Bust the cache. Called by post-purchase flows (e.g. the Welcome page) so
 * the next consumer of the hook gets a fresh entitlement read.
 */
export function invalidateLockerRoomEntitlement() {
  _cached = null;
  _cachedAt = 0;
  _inflight = null;
}

export function useLockerRoomEntitlement() {
  const [state, setState] = useState(() => ({
    isPro: _cached?.isPro ?? false,
    source: _cached?.source ?? 'free',
    current_period_end: _cached?.current_period_end ?? null,
    plan_key: _cached?.plan_key ?? null,
    status: _cached?.status ?? null,
    loading: !_cached,
  }));

  const load = useCallback(() => {
    let active = true;
    setState((s) => ({ ...s, loading: true }));
    fetchEntitlement().then((data) => {
      if (!active) return;
      setState({
        isPro: Boolean(data?.isPro),
        source: data?.source || 'free',
        current_period_end: data?.current_period_end || null,
        plan_key: data?.plan_key || null,
        status: data?.status || null,
        loading: false,
      });
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const cancel = load();
    return cancel;
  }, [load]);

  const refresh = useCallback(() => {
    invalidateLockerRoomEntitlement();
    load();
  }, [load]);

  return { ...state, refresh };
}

export default useLockerRoomEntitlement;
